import type { Driver } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { IconName } from '@cobliteam/colib-ui/Icon';
import type { Vehicle, VehicleGroup } from '@cobliteam/common-types';
import type { PlainGeofence } from '@cobliteam/common-types/src/geofenceV3';
import type { PageDef } from '@cobliteam/dashboard/src/dashboardDef';
import type Fuse from 'fuse.js';
import type { ReactNode } from 'react';

export type ColorKeys = 'gray' | 'primary' | 'warning' | 'success' | 'accessory-purple' | 'accessory-sky';

export enum EnumSidebar {
  ALL = 'all',
  FLEET = 'fleet',
  PAGES = 'pages',
}

export enum EnumCategory {
  ALL = 'all',
  VEHICLES = 'vehicles',
  DRIVERS = 'drivers',
  DEVICES = 'devices',
  VEHICLE_GROUPS = 'vehicleGroups',
  GEOFENCES = 'geofences',
  PAGES = 'pages',
}

export enum EnumCategoryType {
  ITEMS = 'items',
  IA = 'ia',
}

export type Sidebar = SidebarGroup[];

export type SidebarGroup = {
  id: EnumSidebar;
  name: string;
  hideTitle?: boolean;
  categories: Category[];
};

export type Category = {
  id: EnumCategory;
  name: string;
  label: string;
  icon: IconName;
  type: EnumCategoryType;
  color: ColorKeys;
  labelKey: string;
  loading?: boolean;
  componentItem?: (props: { item: DataWithCategory }) => ReactNode;
  componentFilters?: (props: { category: Category }) => ReactNode;
  componentDetails?: (props: { item: DataWithCategory }) => ReactNode;
};

export type VehicleWithCategory = Vehicle & { category: Category; tagsIndex: string; device: DeviceFragment; driver: Driver; groups: VehicleGroup[] };
export type DriverWithCategory = Driver & { category: Category; vehicle: Vehicle };
export type VehicleGroupWithCategory = VehicleGroup & { category: Category; device: DeviceFragment; vehicles: Vehicle[] };
export type DeviceWithCategory = DeviceFragment & { category: Category; vehicle: Vehicle; tagsIndex: string };
export type GeofenceWithCategory = PlainGeofence & { category: Category };
export type PageWithCategory = PageDef & { category: Category };

export type DataWithCategory = VehicleWithCategory | DriverWithCategory | VehicleGroupWithCategory | DeviceWithCategory | GeofenceWithCategory | PageWithCategory;

export type TCounts = Record<EnumCategory, number>;
export type TData = DataWithCategory[];
export type { DeviceFragment, Driver, Fuse, PlainGeofence, Vehicle, VehicleGroup };

export enum EnumDeviceType {
  REMOVABLE = 'Removível',
  PLUG_AND_PLAY_IBUTTON = 'Removível com identificador',
  INSTALLED = 'Instalado',
  BLOCKER = 'Instalado com bloqueador',
  IMMOBILIZER = 'Instalado com trava de ignição',
  IBUTTON = 'Instalado com identificador',
  IBUTTON_IMMOBILIZER = 'Instalado com trava de ignição',
  OBD = 'OBD',
  DASHCAM = 'Câmera',
  UNKNOWN = 'Outros',
}

export enum EnumDeviceGeneralTypeIcon {
  REMOVABLE = 'cpu',
  PLUG_AND_PLAY_IBUTTON = 'key',
  INSTALLED = 'cpu',
  BLOCKER = 'lock',
  IMMOBILIZER = 'power-off',
  IBUTTON = 'key',
  IBUTTON_IMMOBILIZER = 'key',
  OBD = 'router',
  DASHCAM = 'camera',
  UNKNOWN = 'help-circle',
}
